/**
 * Real API Andes Access Service — calls backend via /api/andes/*
 */
import type {
  PermissionRequest,
  Subscription,
  SubscriptionTarget,
  CreatePermissionRequestInput,
  CreateSubscriptionInput,
  GetPermissionRequestsParams,
  GetSubscriptionsParams,
  PermissionStatus,
} from '../../../types/starnexus/andesAccess';
import { apiUrl, apiFetch } from '../../../utils/apiClient';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }
  return res.json();
}

export const realAndesAccessService = {
  // Permission Requests
  async getPermissionRequests(params?: GetPermissionRequestsParams): Promise<PermissionRequest[]> {
    const qs = new URLSearchParams();
    if (params?.status && params.status !== 'all') qs.set('status', params.status);
    if (params?.searchText) qs.set('search', params.searchText);
    if (params?.datasetId) qs.set('datasetId', params.datasetId);
    if (params?.identityType) qs.set('identityType', params.identityType);
    const query = qs.toString();
    return request(`/andes/permissions${query ? `?${query}` : ''}`);
  },

  async getPermissionRequest(requestId: string): Promise<PermissionRequest | null> {
    try { return await request(`/andes/permissions/${requestId}`); }
    catch { return null; }
  },

  async createPermissionRequest(input: CreatePermissionRequestInput): Promise<PermissionRequest[]> {
    return request('/andes/permissions', { method: 'POST', body: JSON.stringify(input) });
  },

  async updatePermissionRequestStatus(requestId: string, status: PermissionStatus, reviewNotes?: string): Promise<PermissionRequest | null> {
    try { return await request(`/andes/permissions/${requestId}/status`, { method: 'PATCH', body: JSON.stringify({ status, reviewNotes }) }); }
    catch { return null; }
  },

  async bulkUpdatePermissionStatus(requestIds: string[], status: PermissionStatus): Promise<{ success: string[]; failed: string[] }> {
    return request('/andes/permissions/bulk-status', { method: 'POST', body: JSON.stringify({ requestIds, status }) });
  },

  // Subscriptions
  async getSubscriptions(params?: GetSubscriptionsParams): Promise<Subscription[]> {
    const qs = new URLSearchParams();
    if (params?.targetType && params.targetType !== 'all') qs.set('targetType', params.targetType);
    if (params?.status && params.status !== 'all') qs.set('status', params.status);
    if (params?.searchText) qs.set('search', params.searchText);
    if (params?.datasetId) qs.set('datasetId', params.datasetId);
    const query = qs.toString();
    return request(`/andes/subscriptions${query ? `?${query}` : ''}`);
  },

  async getSubscription(subscriptionId: string): Promise<Subscription | null> {
    try { return await request(`/andes/subscriptions/${subscriptionId}`); }
    catch { return null; }
  },

  async createSubscription(input: CreateSubscriptionInput): Promise<Subscription[]> {
    return request('/andes/subscriptions', { method: 'POST', body: JSON.stringify(input) });
  },

  async deleteSubscription(subscriptionId: string): Promise<boolean> {
    try { await request(`/andes/subscriptions/${subscriptionId}`, { method: 'DELETE' }); return true; }
    catch { return false; }
  },

  async bulkDeleteSubscriptions(subscriptionIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    return request('/andes/subscriptions/bulk-delete', { method: 'POST', body: JSON.stringify({ subscriptionIds }) });
  },

  // Subscription Targets
  async getSubscriptionTargets(): Promise<SubscriptionTarget[]> {
    return request('/andes/subscription-targets');
  },

  async getSubscriptionTarget(targetId: string): Promise<SubscriptionTarget | null> {
    try { return await request(`/andes/subscription-targets/${targetId}`); }
    catch { return null; }
  },

  async createSubscriptionTarget(target: Omit<SubscriptionTarget, 'targetId'>): Promise<SubscriptionTarget> {
    return request('/andes/subscription-targets', { method: 'POST', body: JSON.stringify(target) });
  },

  async updateSubscriptionTarget(targetId: string, updates: Partial<Omit<SubscriptionTarget, 'targetId'>>): Promise<SubscriptionTarget | null> {
    try { return await request(`/andes/subscription-targets/${targetId}`, { method: 'PUT', body: JSON.stringify(updates) }); }
    catch { return null; }
  },

  async deleteSubscriptionTarget(targetId: string): Promise<boolean> {
    try { await request(`/andes/subscription-targets/${targetId}`, { method: 'DELETE' }); return true; }
    catch { return false; }
  },

  // Andes Dataset Discovery (from /api/andes/* routes)
  async getDatasets(search?: string): Promise<Array<{ name: string; provider: string; version: number }>> {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return request(`/andes/datasets${qs}`);
  },

  async getDatasetSchema(datasetName: string): Promise<Record<string, unknown>> {
    return request(`/andes/datasets/${encodeURIComponent(datasetName)}/schema`);
  },

  async getCompleteness(datasetName: string): Promise<Record<string, unknown>> {
    return request(`/andes/completeness/${encodeURIComponent(datasetName)}`);
  },

  async getLineage(datasetName: string): Promise<Record<string, unknown>> {
    return request(`/andes/lineage/${encodeURIComponent(datasetName)}`);
  },

  // Utility
  async getCounts(): Promise<{ pendingPermissions: number; activeSubscriptions: number; failedSubscriptions: number }> {
    return request('/andes/counts');
  },

  reset(): void { /* no-op for real service */ },
};
